import { isHeicContentType, prepareHeicImage } from './prepareHeicImage'
import { MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION } from './imageWorkspaceSourceDescriptor'

type CachedImageWorkspaceSource = Readonly<{
  blob: Blob
  objectUrl: string
  renderImage: HTMLImageElement
}>

type ImageWorkspaceSourceCacheEntry = {
  cacheKey: string
  controller: AbortController
  promise: Promise<CachedImageWorkspaceSource>
  source: CachedImageWorkspaceSource | undefined
  consumerCount: number
  lastReleasedOrder: number
  evictionTimer: ReturnType<typeof setTimeout> | undefined
  disposed: boolean
}

export type ImageWorkspaceSourceLease = Readonly<{
  blob: Blob
  objectUrl: string
  renderImage: HTMLImageElement
  release: () => void
}>

type ImageWorkspaceSourceOptions = Readonly<{
  resizeToFit?: boolean
}>

const MAX_RELEASED_SOURCE_COUNT = 3
const SOURCE_IDLE_TTL_MS = 2 * 60 * 1_000

const sourceCache = new Map<string, ImageWorkspaceSourceCacheEntry>()
let releaseOrder = 0

export class ImageWorkspaceSourceTooLargeError extends Error {
  constructor() {
    super('Prepared image dimensions are not supported')
    this.name = 'ImageWorkspaceSourceTooLargeError'
  }
}

export class ImageWorkspaceSourceDecodeError extends Error {
  constructor(error: unknown) {
    super(error instanceof Error ? error.message : 'Image decode failed')
    this.name = 'ImageWorkspaceSourceDecodeError'
  }
}

const createAbortError = (): DOMException =>
  new DOMException('Image source loading was cancelled', 'AbortError')

const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError'

const hasValidDecodedDimensions = (image: HTMLImageElement): boolean =>
  Number.isFinite(image.naturalWidth) &&
  Number.isFinite(image.naturalHeight) &&
  image.naturalWidth > 0 &&
  image.naturalHeight > 0

const resizeDecodedSource = (
  blob: Blob,
  image: HTMLImageElement,
): Promise<Blob> => {
  const scale = Math.min(
    1,
    MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION / image.naturalWidth,
    MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION / image.naturalHeight,
  )
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.floor(image.naturalWidth * scale))
  canvas.height = Math.max(1, Math.floor(image.naturalHeight * scale))
  const context = canvas.getContext('2d')
  if (!context) {
    return Promise.reject(new Error('Canvas rendering is not available'))
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height)
  const mimeType =
    blob.type === 'image/jpeg' ||
    blob.type === 'image/png' ||
    blob.type === 'image/webp'
      ? blob.type
      : 'image/jpeg'

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (resizedBlob) => {
        if (resizedBlob) {
          resolve(resizedBlob)
        } else {
          reject(new Error('The image could not be resized'))
        }
      },
      mimeType,
      0.92,
    )
  })
}

const loadSource = async (
  url: string,
  signal: AbortSignal,
  preparationUrl: string | undefined,
  resizeToFit: boolean,
): Promise<CachedImageWorkspaceSource> => {
  let objectUrl: string | undefined

  try {
    // Bypass thumbnail cache entries that may lack the CORS headers required by fetch.
    let response: Response
    let usesPreparation = false
    try {
      response = await fetch(url, { cache: 'reload', signal })
    } catch (error) {
      if (signal.aborted || !preparationUrl) throw error
      usesPreparation = true
      response = await fetch(preparationUrl, { cache: 'reload', signal })
    }
    if (
      preparationUrl &&
      !usesPreparation &&
      !isHeicContentType(response.headers.get('content-type'))
    ) {
      await response.body?.cancel()
      response = await fetch(preparationUrl, { cache: 'reload', signal })
    }
    const isHeic = isHeicContentType(response.headers.get('content-type'))
    if (!response.ok) throw new Error('Image download failed')

    const sourceBlob = await response.blob()
    let blob = isHeic ? await prepareHeicImage(sourceBlob, signal) : sourceBlob
    if (signal.aborted) throw createAbortError()

    objectUrl = URL.createObjectURL(blob)
    let renderImage = new Image()
    renderImage.src = objectUrl
    try {
      await renderImage.decode()
    } catch (error) {
      if (isAbortError(error)) throw error
      throw new ImageWorkspaceSourceDecodeError(error)
    }

    if (signal.aborted) throw createAbortError()
    if (!hasValidDecodedDimensions(renderImage)) {
      throw new ImageWorkspaceSourceDecodeError(
        new Error('Prepared image dimensions are not supported'),
      )
    }
    if (
      renderImage.naturalWidth > MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION ||
      renderImage.naturalHeight > MAX_IMAGE_WORKSPACE_SOURCE_DIMENSION
    ) {
      if (!resizeToFit) throw new ImageWorkspaceSourceTooLargeError()

      blob = await resizeDecodedSource(blob, renderImage)
      if (signal.aborted) throw createAbortError()

      URL.revokeObjectURL(objectUrl)
      objectUrl = URL.createObjectURL(blob)
      renderImage = new Image()
      renderImage.src = objectUrl
      try {
        await renderImage.decode()
      } catch (error) {
        if (isAbortError(error)) throw error
        throw new ImageWorkspaceSourceDecodeError(error)
      }

      if (signal.aborted) throw createAbortError()
      if (!hasValidDecodedDimensions(renderImage)) {
        throw new ImageWorkspaceSourceDecodeError(
          new Error('Prepared image dimensions are not supported'),
        )
      }
    }

    return { blob, objectUrl, renderImage }
  } catch (error) {
    if (objectUrl !== undefined) URL.revokeObjectURL(objectUrl)
    throw error
  }
}

const clearEvictionTimer = (entry: ImageWorkspaceSourceCacheEntry): void => {
  if (entry.evictionTimer === undefined) return

  clearTimeout(entry.evictionTimer)
  entry.evictionTimer = undefined
}

const disposeEntry = (entry: ImageWorkspaceSourceCacheEntry): void => {
  if (entry.disposed) return

  entry.disposed = true
  clearEvictionTimer(entry)
  if (sourceCache.get(entry.cacheKey) === entry) {
    sourceCache.delete(entry.cacheKey)
  }

  const source = entry.source
  entry.source = undefined
  if (source) {
    URL.revokeObjectURL(source.objectUrl)
  } else {
    entry.controller.abort()
  }
}

const evictReleasedSources = (): void => {
  const releasedEntries = Array.from(sourceCache.values())
    .filter(
      (entry) =>
        !entry.disposed &&
        entry.consumerCount === 0 &&
        entry.source !== undefined,
    )
    .toSorted(
      (firstEntry, secondEntry) =>
        firstEntry.lastReleasedOrder - secondEntry.lastReleasedOrder,
    )

  const evictionCount = releasedEntries.length - MAX_RELEASED_SOURCE_COUNT
  if (evictionCount <= 0) return

  releasedEntries.slice(0, evictionCount).forEach(disposeEntry)
}

const scheduleIdleEviction = (entry: ImageWorkspaceSourceCacheEntry): void => {
  clearEvictionTimer(entry)
  entry.evictionTimer = setTimeout(() => {
    entry.evictionTimer = undefined
    if (
      entry.consumerCount === 0 &&
      entry.source !== undefined &&
      sourceCache.get(entry.cacheKey) === entry
    ) {
      disposeEntry(entry)
    }
  }, SOURCE_IDLE_TTL_MS)
}

const releaseEntry = (entry: ImageWorkspaceSourceCacheEntry): void => {
  entry.consumerCount = Math.max(0, entry.consumerCount - 1)
  if (entry.disposed || entry.consumerCount > 0) return

  if (!entry.source) {
    disposeEntry(entry)
    return
  }

  releaseOrder += 1
  entry.lastReleasedOrder = releaseOrder
  scheduleIdleEviction(entry)
  evictReleasedSources()
}

const createEntry = (
  cacheKey: string,
  url: string,
  preparationUrl: string | undefined,
  resizeToFit: boolean,
): ImageWorkspaceSourceCacheEntry => {
  const controller = new AbortController()
  const promise = loadSource(
    url,
    controller.signal,
    preparationUrl,
    resizeToFit,
  )
  const entry: ImageWorkspaceSourceCacheEntry = {
    cacheKey,
    controller,
    promise,
    source: undefined,
    consumerCount: 0,
    lastReleasedOrder: 0,
    evictionTimer: undefined,
    disposed: false,
  }

  sourceCache.set(cacheKey, entry)
  const settleEntry = async (): Promise<void> => {
    try {
      const source = await promise
      if (entry.disposed || sourceCache.get(cacheKey) !== entry) {
        URL.revokeObjectURL(source.objectUrl)
        return
      }

      entry.source = source
    } catch {
      if (sourceCache.get(cacheKey) === entry) sourceCache.delete(cacheKey)
      clearEvictionTimer(entry)
      entry.disposed = true
    }
  }
  void settleEntry()

  return entry
}

const retainEntry = (entry: ImageWorkspaceSourceCacheEntry): void => {
  clearEvictionTimer(entry)
  entry.consumerCount += 1
}

export const acquireImageWorkspaceSource = async (
  url: string,
  signal?: AbortSignal,
  preparationUrl?: string,
  { resizeToFit = false }: ImageWorkspaceSourceOptions = {},
): Promise<ImageWorkspaceSourceLease> => {
  if (signal?.aborted) throw createAbortError()

  const cacheKey = JSON.stringify([url, preparationUrl ?? null, resizeToFit])
  const cachedEntry = sourceCache.get(cacheKey)
  const entry =
    cachedEntry && !cachedEntry.disposed
      ? cachedEntry
      : createEntry(cacheKey, url, preparationUrl, resizeToFit)
  retainEntry(entry)

  let released = false
  let rejectForAbort: ((reason?: unknown) => void) | undefined

  const release = (): void => {
    if (released) return

    released = true
    signal?.removeEventListener('abort', handleAbort)
    releaseEntry(entry)
  }

  const handleAbort = (): void => {
    const reject = rejectForAbort
    rejectForAbort = undefined
    release()
    reject?.(createAbortError())
  }

  const abortPromise = signal
    ? new Promise<never>((_resolve, reject) => {
        rejectForAbort = reject
      })
    : undefined

  signal?.addEventListener('abort', handleAbort, { once: true })
  if (signal?.aborted) handleAbort()

  try {
    const source = abortPromise
      ? await Promise.race([entry.promise, abortPromise])
      : await entry.promise
    rejectForAbort = undefined

    if (released) throw createAbortError()

    return {
      blob: source.blob,
      objectUrl: source.objectUrl,
      renderImage: source.renderImage,
      release,
    }
  } catch (error) {
    rejectForAbort = undefined
    release()
    throw error
  }
}

export const resetImageWorkspaceSourceCacheForTests = (): void => {
  const entries = Array.from(sourceCache.values())
  sourceCache.clear()
  entries.forEach(disposeEntry)
  releaseOrder = 0
}
